/**
 * This file was automatically generated by ontology-generator.
 * DO NOT MODIFY IT BY HAND. Instead, modify the source JSON files in
 * tools/ontology-generator/src/nodespec/ and re-run the generator.
 */
import type { NodeLabel } from '../../types/schema'
import type { RelationshipType } from './relationships'

/**
 * One allowed (source, target) label pair for a relationship type. Multiple
 * entries on the same RelationshipPolicy express a disjunction.
 */
export interface EndpointConstraint {
  source: NodeLabel
  target: NodeLabel
}

/**
 * Validation policy for a single relationship type, consumed by
 * `validateCypherWrite` in @atbash-labs/ontology-cypher-validator.
 *
 * - `endpoints`: empty when `unconstrained` is true; otherwise lists every
 *   legal { source, target } pair.
 * - `symmetric`: when true, the validator accepts the rel in either direction.
 * - `unconstrained`: when true, the validator skips endpoint checks entirely.
 */
export interface RelationshipPolicy {
  endpoints: readonly EndpointConstraint[]
  symmetric: boolean
  unconstrained: boolean
}

export const RELATIONSHIP_POLICY: Readonly<Record<RelationshipType, RelationshipPolicy>> = {
  HAS_SITE: {
    endpoints: [{ source: 'Organization', target: 'Site' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_PROCESS: {
    endpoints: [{ source: 'Organization', target: 'Process' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_COMPUTE: {
    endpoints: [{ source: 'Site', target: 'Compute' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_SOAP_ENDPOINT: {
    endpoints: [
      { source: 'Organization', target: 'SoapEndpoint' },
      { source: 'Site', target: 'SoapEndpoint' }
    ],
    symmetric: false,
    unconstrained: false
  },
  USES_CONTRACT: {
    endpoints: [{ source: 'SoapEndpoint', target: 'SoapContract' }],
    symmetric: false,
    unconstrained: false
  },
  DECLARES_OPERATION: {
    endpoints: [{ source: 'SoapContract', target: 'SoapOperation' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_LINE: {
    endpoints: [{ source: 'Site', target: 'Line' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_PRODUCTION_GOAL: {
    endpoints: [{ source: 'Line', target: 'ProductionGoal' }],
    symmetric: false,
    unconstrained: false
  },
  ACCOMPLISHES: {
    endpoints: [{ source: 'Site', target: 'Process' }],
    symmetric: false,
    unconstrained: false
  },
  NETWORKS_WITH: {
    endpoints: [{ source: 'Compute', target: 'Compute' }],
    symmetric: true,
    unconstrained: false
  },
  HAS_SUBPROCESS: {
    endpoints: [{ source: 'Process', target: 'Process' }],
    symmetric: false,
    unconstrained: false
  },
  INSTANTIATES: {
    endpoints: [
      { source: 'Line', target: 'Process' },
      { source: 'AOI', target: 'PLCTag' },
      { source: 'DcsController', target: 'BlockType' },
      { source: 'Cee', target: 'BlockType' },
      { source: 'ControlModule', target: 'BlockType' },
      { source: 'FunctionBlock', target: 'BlockType' },
      { source: 'IoModule', target: 'BlockType' },
      { source: 'IoChannel', target: 'BlockType' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_SUBLINE: {
    endpoints: [{ source: 'Line', target: 'Line' }],
    symmetric: false,
    unconstrained: false
  },
  FEEDS: {
    endpoints: [
      { source: 'Line', target: 'Line' },
      { source: 'LineOperation', target: 'LineOperation' },
      { source: 'HumanLabor', target: 'HumanLabor' },
      { source: 'HumanLabor', target: 'Equipment' },
      { source: 'Equipment', target: 'HumanLabor' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_TAG_PROVIDER: {
    endpoints: [{ source: 'Compute', target: 'TagProvider' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_DATABASE: {
    endpoints: [
      { source: 'Compute', target: 'Database' },
      { source: 'Site', target: 'Database' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_PSS_TAG: {
    endpoints: [{ source: 'Database', target: 'PssTag' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_EVENT_JOURNAL: {
    endpoints: [
      { source: 'PssTag', target: 'PssEventJournal' },
      { source: 'Database', target: 'PssEventJournal' }
    ],
    symmetric: false,
    unconstrained: false
  },
  PROVIDES: {
    endpoints: [
      { source: 'TagProvider', target: 'Tag' },
      { source: 'TagProvider', target: 'UDTDefinition' },
      { source: 'TagProvider', target: 'UDTInstance' },
      { source: 'TagProvider', target: 'Folder' },
      { source: 'ControlNamespace', target: 'ControlTag' }
    ],
    symmetric: false,
    unconstrained: false
  },
  CONTAINS: {
    endpoints: [
      { source: 'Folder', target: 'Folder' },
      { source: 'Folder', target: 'Tag' },
      { source: 'Folder', target: 'UDTDefinition' },
      { source: 'Folder', target: 'UDTInstance' },
      { source: 'ControlModule', target: 'FunctionBlock' },
      { source: 'FunctionBlock', target: 'FunctionBlock' },
      { source: 'Cee', target: 'FunctionBlock' },
      { source: 'DcsController', target: 'FunctionBlock' },
      { source: 'IoModule', target: 'IoChannel' },
      { source: 'Site', target: 'Asset' },
      { source: 'Site', target: 'NetworkSegment' },
      { source: 'Asset', target: 'Asset' },
      { source: 'Asset', target: 'NetworkSegment' },
      { source: 'Asset', target: 'NetworkDevice' },
      { source: 'Asset', target: 'Compute' },
      { source: 'NetworkSegment', target: 'NetworkDevice' },
      { source: 'NetworkSegment', target: 'Compute' },
      { source: 'NetworkDevice', target: 'NetworkDevice' },
      { source: 'NetworkDevice', target: 'Compute' },
      { source: 'LogicSolver', target: 'FunctionLogicDiagram' },
      { source: 'FunctionLogicDiagram', target: 'SafetyPoint' }
    ],
    symmetric: false,
    unconstrained: false
  },
  CALCULATED_BY: {
    endpoints: [
      { source: 'Tag', target: 'Tag' },
      { source: 'PLCTag', target: 'PLCTag' }
    ],
    symmetric: false,
    unconstrained: false
  },
  DERIVES_FROM: {
    endpoints: [{ source: 'Tag', target: 'Compute' }],
    symmetric: false,
    unconstrained: false
  },
  CONTAINS_TABLE: {
    endpoints: [{ source: 'Database', target: 'Table' }],
    symmetric: false,
    unconstrained: false
  },
  USES_DATABASE: {
    endpoints: [
      { source: 'NamedQuery', target: 'Database' },
      { source: 'Tag', target: 'Database' },
      { source: 'QueryTag', target: 'Database' }
    ],
    symmetric: false,
    unconstrained: false
  },
  USES_TABLE: {
    endpoints: [{ source: 'NamedQuery', target: 'Table' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_EQUIPMENT: {
    endpoints: [
      { source: 'Line', target: 'Equipment' },
      { source: 'Equipment', target: 'Equipment' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_PART: {
    endpoints: [
      { source: 'Equipment', target: 'Part' },
      { source: 'Part', target: 'Part' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_PROPERTY: {
    endpoints: [{ source: 'Equipment', target: 'EquipmentProperty' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_OBSERVABLE: {
    endpoints: [
      { source: 'Equipment', target: 'Observable' },
      { source: 'Part', target: 'Observable' },
      { source: 'Line', target: 'Observable' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_SETPOINT: {
    endpoints: [
      { source: 'Equipment', target: 'Setpoint' },
      { source: 'Part', target: 'Setpoint' }
    ],
    symmetric: false,
    unconstrained: false
  },
  CONNECTS_TO: {
    endpoints: [{ source: 'Equipment', target: 'Equipment' }],
    symmetric: false,
    unconstrained: false
  },
  MEASURED_BY: {
    endpoints: [
      { source: 'Observable', target: 'Tag' },
      { source: 'EquipmentProperty', target: 'Tag' }
    ],
    symmetric: false,
    unconstrained: false
  },
  TRACKS_GOAL: {
    endpoints: [{ source: 'Observable', target: 'ProductionGoal' }],
    symmetric: false,
    unconstrained: false
  },
  TARGET_VALUE_FROM: {
    endpoints: [{ source: 'ProductionGoal', target: 'Tag' }],
    symmetric: false,
    unconstrained: false
  },
  SET_BY: {
    endpoints: [
      { source: 'Setpoint', target: 'Tag' },
      { source: 'ControlSetpoint', target: 'ControlTag' }
    ],
    symmetric: false,
    unconstrained: false
  },
  STORES: {
    endpoints: [{ source: 'Table', target: 'Observable' }],
    symmetric: false,
    unconstrained: false
  },
  MATERIALIZES: {
    endpoints: [
      { source: 'Equipment', target: 'UDTInstance' },
      { source: 'Line', target: 'UDTInstance' }
    ],
    symmetric: false,
    unconstrained: false
  },
  MERGED_WITH: {
    endpoints: [{ source: 'Equipment', target: 'Equipment' }],
    symmetric: true,
    unconstrained: false
  },
  BELONGS_TO: {
    endpoints: [
      { source: 'Program', target: 'Controller' },
      { source: 'Routine', target: 'Program' },
      { source: 'Routine', target: 'AOI' },
      { source: 'AOI', target: 'Controller' },
      { source: 'PLCDataType', target: 'Controller' },
      { source: 'View', target: 'Project' },
      { source: 'UDTMember', target: 'UDTDefinition' },
      { source: 'AuditProfile', target: 'Compute' },
      { source: 'ScheduleProfile', target: 'Compute' },
      { source: 'Holiday', target: 'Compute' },
      { source: 'Asset', target: 'EmdbProject' },
      { source: 'NetworkSegment', target: 'EmdbProject' },
      { source: 'NetworkDevice', target: 'EmdbProject' },
      { source: 'Compute', target: 'EmdbProject' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_TAG: {
    endpoints: [
      { source: 'Controller', target: 'PLCTag' },
      { source: 'Program', target: 'PLCTag' },
      { source: 'Routine', target: 'PLCTag' },
      { source: 'AOI', target: 'PLCTag' }
    ],
    symmetric: false,
    unconstrained: false
  },
  READS_TAG: {
    endpoints: [{ source: 'Routine', target: 'PLCTag' }],
    symmetric: false,
    unconstrained: false
  },
  WRITES_TAG: {
    endpoints: [{ source: 'Routine', target: 'PLCTag' }],
    symmetric: false,
    unconstrained: false
  },
  USES_TYPE: {
    endpoints: [],
    symmetric: false,
    unconstrained: true
  },
  TRIGGERS: {
    endpoints: [
      { source: 'PLCTag', target: 'PLCTag' },
      { source: 'RobotIOSignal', target: 'VisionProcess' }
    ],
    symmetric: false,
    unconstrained: false
  },
  GATES: {
    endpoints: [{ source: 'PLCTag', target: 'PLCTag' }],
    symmetric: false,
    unconstrained: false
  },
  INHIBITS: {
    endpoints: [{ source: 'PLCTag', target: 'PLCTag' }],
    symmetric: false,
    unconstrained: false
  },
  CONTROLS: {
    endpoints: [
      { source: 'PLCTag', target: 'PLCTag' },
      { source: 'ControlPattern', target: 'PLCTag' },
      { source: 'RobotController', target: 'Equipment' }
    ],
    symmetric: false,
    unconstrained: false
  },
  COORDINATES: {
    endpoints: [
      { source: 'Controller', target: 'RobotController' },
      { source: 'Controller', target: 'VisionSystem' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_PATTERN: {
    endpoints: [{ source: 'Controller', target: 'ControlPattern' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_FLOW: {
    endpoints: [{ source: 'PLCTag', target: 'DataFlow' }],
    symmetric: false,
    unconstrained: false
  },
  SAFETY_CRITICAL: {
    endpoints: [],
    symmetric: false,
    unconstrained: true
  },
  RELATES_TO: {
    endpoints: [],
    symmetric: false,
    unconstrained: true
  },
  HAS_MEMBER: {
    endpoints: [
      { source: 'PLCDataType', target: 'PLCDataTypeMember' },
      { source: 'UDTDefinition', target: 'UDTMember' },
      { source: 'UDTDefinition', target: 'UDTParameter' },
      { source: 'UDTInstance', target: 'Tag' },
      { source: 'UDTInstance', target: 'UDTInstance' },
      { source: 'UDTInstance', target: 'UDTParameter' },
      { source: 'UDTMember', target: 'UDTMember' },
      { source: 'UDTMember', target: 'UDTParameter' }
    ],
    symmetric: false,
    unconstrained: false
  },
  INSTANCE_OF: {
    endpoints: [
      { source: 'UDTInstance', target: 'UDTDefinition' },
      { source: 'UDTMember', target: 'UDTDefinition' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_COMPONENT: {
    endpoints: [{ source: 'View', target: 'ViewComponent' }],
    symmetric: false,
    unconstrained: false
  },
  BINDS_TO: {
    endpoints: [{ source: 'ViewComponent', target: 'Tag' }],
    symmetric: false,
    unconstrained: false
  },
  INHERITS_FROM: {
    endpoints: [{ source: 'UDTDefinition', target: 'UDTDefinition' }],
    symmetric: false,
    unconstrained: false
  },
  CALLS_FUNCTION: {
    endpoints: [
      { source: 'Script', target: 'Script' },
      { source: 'GatewayEvent', target: 'Script' },
      { source: 'GatewayEventHandler', target: 'Script' }
    ],
    symmetric: false,
    unconstrained: false
  },
  USES_QUERY: {
    endpoints: [
      { source: 'ViewComponent', target: 'NamedQuery' },
      { source: 'UDTMember', target: 'NamedQuery' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_EVENT_SCRIPT: {
    endpoints: [{ source: 'Project', target: 'GatewayEvent' }],
    symmetric: false,
    unconstrained: false
  },
  VISUALIZES: {
    endpoints: [
      { source: 'View', target: 'Equipment' },
      { source: 'View', target: 'Line' },
      { source: 'View', target: 'Process' }
    ],
    symmetric: false,
    unconstrained: false
  },
  REFERENCES: {
    endpoints: [],
    symmetric: false,
    unconstrained: true
  },
  MAPS_TO_TAG: {
    endpoints: [
      { source: 'Equipment', target: 'Tag' },
      { source: 'Equipment', target: 'PssTag' },
      { source: 'PLCTag', target: 'Tag' },
      { source: 'RobotIOSignal', target: 'Tag' }
    ],
    symmetric: false,
    unconstrained: false
  },
  MAPS_TO_CONTROL_TAG: {
    endpoints: [
      { source: 'Equipment', target: 'ControlTag' },
      { source: 'Tag', target: 'ControlTag' }
    ],
    symmetric: false,
    unconstrained: false
  },
  MAPS_TO_TYPE: {
    endpoints: [{ source: 'PLCDataType', target: 'UDTDefinition' }],
    symmetric: false,
    unconstrained: false
  },
  OF_TYPE: {
    endpoints: [{ source: 'PLCTag', target: 'PLCDataType' }],
    symmetric: false,
    unconstrained: false
  },
  EXHIBITS_SYMPTOM: {
    endpoints: [
      { source: 'Equipment', target: 'FaultSymptom' },
      { source: 'Part', target: 'FaultSymptom' },
      { source: 'Area', target: 'FaultSymptom' }
    ],
    symmetric: false,
    unconstrained: false
  },
  CAN_OBSERVE: {
    endpoints: [
      { source: 'Observer', target: 'Equipment' },
      { source: 'Observer', target: 'Part' },
      { source: 'Observer', target: 'Area' },
      { source: 'Observer', target: 'Site' }
    ],
    symmetric: false,
    unconstrained: false
  },
  INDICATES: {
    endpoints: [{ source: 'Equipment', target: 'Observable' }],
    symmetric: false,
    unconstrained: false
  },
  INDUCES: {
    endpoints: [{ source: 'Problem', target: 'FaultSymptom' }],
    symmetric: false,
    unconstrained: false
  },
  CAUSED_BY: {
    endpoints: [{ source: 'Problem', target: 'FaultCause' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_INTENT: {
    endpoints: [{ source: 'OperatorPhrase', target: 'Intent' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_PHRASE: {
    endpoints: [{ source: 'Intent', target: 'OperatorPhrase' }],
    symmetric: false,
    unconstrained: false
  },
  USES_EQUIPMENT: {
    endpoints: [
      { source: 'Recipe', target: 'Equipment' },
      { source: 'Phase', target: 'Equipment' },
      { source: 'LineOperation', target: 'Equipment' },
      { source: 'HumanLabor', target: 'Equipment' }
    ],
    symmetric: false,
    unconstrained: false
  },
  RAN_ON: {
    endpoints: [
      { source: 'Batch', target: 'Equipment' },
      { source: 'Operation', target: 'Equipment' }
    ],
    symmetric: false,
    unconstrained: false
  },
  FOR_PRODUCT: {
    endpoints: [
      { source: 'Recipe', target: 'Product' },
      { source: 'WorkOrder', target: 'Product' },
      { source: 'ProductionGoal', target: 'Product' },
      { source: 'CustomerOrder', target: 'Product' },
      { source: 'DemandForecast', target: 'Product' },
      { source: 'DemandHistory', target: 'Product' }
    ],
    symmetric: false,
    unconstrained: false
  },
  FULFILLED_BY: {
    endpoints: [{ source: 'CustomerOrder', target: 'WorkOrder' }],
    symmetric: false,
    unconstrained: false
  },
  LOCATED_IN: {
    endpoints: [
      { source: 'Equipment', target: 'FunctionalLocation' },
      { source: 'Part', target: 'FunctionalLocation' },
      { source: 'Equipment', target: 'Area' },
      { source: 'Part', target: 'Area' },
      { source: 'Process', target: 'Area' }
    ],
    symmetric: false,
    unconstrained: false
  },
  BATCH_OF: {
    endpoints: [{ source: 'Batch', target: 'WorkOrder' }],
    symmetric: false,
    unconstrained: false
  },
  PRODUCES: {
    endpoints: [
      { source: 'Recipe', target: 'Product' },
      { source: 'Recipe', target: 'Material' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_OPERATION: {
    endpoints: [{ source: 'WorkOrder', target: 'Operation' }],
    symmetric: false,
    unconstrained: false
  },
  EXECUTED_ON: {
    endpoints: [{ source: 'Operation', target: 'Equipment' }],
    symmetric: false,
    unconstrained: false
  },
  AFFECTS_BATCH: {
    endpoints: [{ source: 'ProcessDeviation', target: 'Batch' }],
    symmetric: false,
    unconstrained: false
  },
  OCCURRED_ON: {
    endpoints: [{ source: 'ProcessDeviation', target: 'Equipment' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_QUALITY_RESULT: {
    endpoints: [{ source: 'Batch', target: 'QualityResult' }],
    symmetric: false,
    unconstrained: false
  },
  PART_OF: {
    endpoints: [
      { source: 'FunctionalLocation', target: 'FunctionalLocation' },
      { source: 'Area', target: 'Area' },
      { source: 'Area', target: 'Site' },
      { source: 'SrsSection', target: 'Site' },
      { source: 'ReferenceDocument', target: 'Site' },
      { source: 'HazardScenario', target: 'Site' },
      { source: 'LogicSolver', target: 'Site' }
    ],
    symmetric: false,
    unconstrained: false
  },
  CONSUMES: {
    endpoints: [
      { source: 'Recipe', target: 'Material' },
      { source: 'Phase', target: 'Material' },
      { source: 'Batch', target: 'Material' }
    ],
    symmetric: false,
    unconstrained: false
  },
  AFFECTS: {
    endpoints: [],
    symmetric: false,
    unconstrained: true
  },
  RELATED_TO: {
    endpoints: [],
    symmetric: false,
    unconstrained: true
  },
  PROCEDURE_FOR: {
    endpoints: [
      { source: 'Procedure', target: 'Equipment' },
      { source: 'Procedure', target: 'Line' },
      { source: 'Procedure', target: 'Process' },
      { source: 'Procedure', target: 'LineOperation' },
      { source: 'Procedure', target: 'HumanLabor' }
    ],
    symmetric: false,
    unconstrained: false
  },
  APPLIES_TO_PRODUCT: {
    endpoints: [{ source: 'Procedure', target: 'Product' }],
    symmetric: false,
    unconstrained: false
  },
  REQUIRES_PART: {
    endpoints: [{ source: 'Procedure', target: 'Part' }],
    symmetric: false,
    unconstrained: false
  },
  REQUIRES_SKILL_LEVEL: {
    endpoints: [{ source: 'Procedure', target: 'Role' }],
    symmetric: false,
    unconstrained: false
  },
  SUPPLIED_BY: {
    endpoints: [{ source: 'Part', target: 'Vendor' }],
    symmetric: false,
    unconstrained: false
  },
  MANUFACTURED_BY: {
    endpoints: [{ source: 'Part', target: 'Vendor' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_UTILITY: {
    endpoints: [
      { source: 'Site', target: 'Utility' },
      { source: 'Line', target: 'Utility' }
    ],
    symmetric: false,
    unconstrained: false
  },
  REQUIRES_UTILITY: {
    endpoints: [{ source: 'Equipment', target: 'Utility' }],
    symmetric: false,
    unconstrained: false
  },
  MAPS_TO_CONTROLLER: {
    endpoints: [
      { source: 'Compute', target: 'Controller' },
      { source: 'Compute', target: 'DcsController' }
    ],
    symmetric: false,
    unconstrained: false
  },
  CONTROLLED_BY: {
    endpoints: [
      { source: 'Equipment', target: 'Controller' },
      { source: 'Line', target: 'Controller' }
    ],
    symmetric: false,
    unconstrained: false
  },
  COMPOSES: {
    endpoints: [{ source: 'ScheduleProfile', target: 'ScheduleProfile' }],
    symmetric: false,
    unconstrained: false
  },
  MONITORED_BY: {
    endpoints: [
      { source: 'Equipment', target: 'Compute' },
      { source: 'Line', target: 'Compute' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_ALARM: {
    endpoints: [
      { source: 'Tag', target: 'Alarm' },
      { source: 'ControlTag', target: 'ControlAlarm' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_ALARM_DEFINITION: {
    endpoints: [{ source: 'UDTDefinition', target: 'Alarm' }],
    symmetric: false,
    unconstrained: false
  },
  HOSTS: {
    endpoints: [
      { source: 'RobotController', target: 'RobotArm' },
      { source: 'Compute', target: 'ControlNamespace' },
      { source: 'DcsController', target: 'Cee' }
    ],
    symmetric: false,
    unconstrained: false
  },
  SUPERVISES: {
    endpoints: [{ source: 'RobotController', target: 'RobotController' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_JOB: {
    endpoints: [
      { source: 'RobotArm', target: 'RobotJob' },
      { source: 'VisionSystem', target: 'VisionJob' }
    ],
    symmetric: false,
    unconstrained: false
  },
  CALLS_JOB: {
    endpoints: [{ source: 'RobotJob', target: 'RobotJob' }],
    symmetric: false,
    unconstrained: false
  },
  READ_BY: {
    endpoints: [{ source: 'RobotIOSignal', target: 'RobotJob' }],
    symmetric: false,
    unconstrained: false
  },
  WRITTEN_BY: {
    endpoints: [{ source: 'RobotIOSignal', target: 'RobotJob' }],
    symmetric: false,
    unconstrained: false
  },
  USED_BY: {
    endpoints: [
      { source: 'RobotCondition', target: 'RobotJob' },
      { source: 'VisionProcess', target: 'RobotJob' },
      { source: 'VisionData', target: 'RobotJob' }
    ],
    symmetric: false,
    unconstrained: false
  },
  DEFINES: {
    endpoints: [
      { source: 'RobotController', target: 'RobotCondition' },
      { source: 'RobotController', target: 'RobotIOSignal' },
      { source: 'RobotController', target: 'VisionProcess' },
      { source: 'RobotController', target: 'VisionData' }
    ],
    symmetric: false,
    unconstrained: false
  },
  OUTPUTS_TO: {
    endpoints: [{ source: 'VisionProcess', target: 'RobotIOSignal' }],
    symmetric: false,
    unconstrained: false
  },
  USES_VISION_DATA: {
    endpoints: [{ source: 'VisionProcess', target: 'VisionData' }],
    symmetric: false,
    unconstrained: false
  },
  MAPS_TO_ROBOT_SIGNAL: {
    endpoints: [{ source: 'PLCTag', target: 'RobotIOSignal' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_IO_TAG: {
    endpoints: [{ source: 'VisionSystem', target: 'PLCTag' }],
    symmetric: false,
    unconstrained: false
  },
  REQUIRES_LABOR: {
    endpoints: [
      { source: 'LineOperation', target: 'HumanLabor' },
      { source: 'Line', target: 'HumanLabor' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_LINE_OPERATION: {
    endpoints: [{ source: 'Line', target: 'LineOperation' }],
    symmetric: false,
    unconstrained: false
  },
  RECOVERS: {
    endpoints: [{ source: 'LineOperation', target: 'LineOperation' }],
    symmetric: false,
    unconstrained: false
  },
  FALLBACK_FOR: {
    endpoints: [{ source: 'LineOperation', target: 'LineOperation' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_CREW_REQUIREMENT: {
    endpoints: [{ source: 'Line', target: 'CrewRequirement' }],
    symmetric: false,
    unconstrained: false
  },
  COVERS: {
    endpoints: [{ source: 'CrewRequirement', target: 'HumanLabor' }],
    symmetric: false,
    unconstrained: false
  },
  STAFFED_BY: {
    endpoints: [
      { source: 'HumanLabor', target: 'Role' },
      { source: 'CrewRequirement', target: 'Role' }
    ],
    symmetric: false,
    unconstrained: false
  },
  SCHEDULED_BY: {
    endpoints: [
      { source: 'LineOperation', target: 'ScheduleProfile' },
      { source: 'HumanLabor', target: 'ScheduleProfile' }
    ],
    symmetric: false,
    unconstrained: false
  },
  TRIGGERED_BY: {
    endpoints: [
      { source: 'LineOperation', target: 'Tag' },
      { source: 'HumanLabor', target: 'Tag' }
    ],
    symmetric: false,
    unconstrained: false
  },
  ASSIGNED_TO: {
    endpoints: [
      { source: 'ControlModule', target: 'Cee' },
      { source: 'IoModule', target: 'DcsController' }
    ],
    symmetric: false,
    unconstrained: false
  },
  MOUNTED_ON: {
    endpoints: [
      { source: 'IoChannel', target: 'IoModule' },
      { source: 'IoModule', target: 'DcsController' }
    ],
    symmetric: false,
    unconstrained: false
  },
  WIRED_TO: {
    endpoints: [
      { source: 'FunctionBlock', target: 'IoChannel' },
      { source: 'ControlTag', target: 'IoChannel' }
    ],
    symmetric: false,
    unconstrained: false
  },
  SIGNAL_FLOWS_TO: {
    endpoints: [{ source: 'FunctionBlock', target: 'FunctionBlock' }],
    symmetric: false,
    unconstrained: false
  },
  EXPOSES: {
    endpoints: [{ source: 'ControlModule', target: 'ControlTag' }],
    symmetric: false,
    unconstrained: false
  },
  PARENT_OF: {
    endpoints: [{ source: 'BlockLibrary', target: 'BlockType' }],
    symmetric: false,
    unconstrained: false
  },
  DERIVED_FROM: {
    endpoints: [{ source: 'BlockType', target: 'BlockType' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_PARAM_GROUP: {
    endpoints: [{ source: 'BlockType', target: 'BlockParamGroup' }],
    symmetric: false,
    unconstrained: false
  },
  PARTNER_OF: {
    endpoints: [{ source: 'DcsController', target: 'DcsController' }],
    symmetric: true,
    unconstrained: false
  },
  SENSED_BY: {
    endpoints: [{ source: 'ProtectiveFunction', target: 'Equipment' }],
    symmetric: false,
    unconstrained: false
  },
  ACTUATES: {
    endpoints: [{ source: 'ProtectiveFunction', target: 'Equipment' }],
    symmetric: false,
    unconstrained: false
  },
  PROTECTS: {
    endpoints: [{ source: 'ProtectiveFunction', target: 'Equipment' }],
    symmetric: false,
    unconstrained: false
  },
  SOLVED_BY: {
    endpoints: [{ source: 'ProtectiveFunction', target: 'LogicSolver' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_PROTECTIVE_FUNCTION: {
    endpoints: [{ source: 'Line', target: 'ProtectiveFunction' }],
    symmetric: false,
    unconstrained: false
  },
  IN_SECTION: {
    endpoints: [{ source: 'ProtectiveFunction', target: 'SrsSection' }],
    symmetric: false,
    unconstrained: false
  },
  CITES: {
    endpoints: [{ source: 'ProtectiveFunction', target: 'ReferenceDocument' }],
    symmetric: false,
    unconstrained: false
  },
  FOR_SCENARIO: {
    endpoints: [{ source: 'ProtectiveFunction', target: 'HazardScenario' }],
    symmetric: false,
    unconstrained: false
  },
  HAS_ACTUATOR: {
    endpoints: [{ source: 'Equipment', target: 'Equipment' }],
    symmetric: false,
    unconstrained: false
  },
  ASSOCIATED_WITH: {
    endpoints: [{ source: 'Equipment', target: 'Equipment' }],
    symmetric: false,
    unconstrained: false
  },
  IMPLEMENTED_BY: {
    endpoints: [
      { source: 'ProtectiveFunction', target: 'SafetyPoint' },
      { source: 'ProtectiveFunction', target: 'FunctionLogicDiagram' }
    ],
    symmetric: false,
    unconstrained: false
  },
  MAPS_TO_SAFETY_POINT: {
    endpoints: [{ source: 'Equipment', target: 'SafetyPoint' }],
    symmetric: false,
    unconstrained: false
  },
  APPLIES_TO: {
    endpoints: [
      { source: 'Problem', target: 'Equipment' },
      { source: 'Problem', target: 'Part' },
      { source: 'Problem', target: 'Line' },
      { source: 'Problem', target: 'Area' },
      { source: 'Problem', target: 'Site' }
    ],
    symmetric: false,
    unconstrained: false
  },
  HAS_SYMPTOM: {
    endpoints: [{ source: 'Problem', target: 'FaultSymptom' }],
    symmetric: false,
    unconstrained: false
  },
  MAY_BE_CAUSED_BY: {
    endpoints: [{ source: 'Problem', target: 'FaultCause' }],
    symmetric: false,
    unconstrained: false
  }
}

/**
 * Node labels that the cypher-write tool must not allow agents to CREATE or
 * MERGE. MATCH on these labels is still permitted so the agent can reference
 * them read-only inside a write query.
 */
export const CYPHER_WRITE_BLOCKED_LABELS: ReadonlySet<NodeLabel> = new Set<NodeLabel>([
  'Alarm',
  'Asset',
  'AuditProfile',
  'BlockParamGroup',
  'Compute',
  'Database',
  'EmdbProject',
  'EquipmentProperty',
  'Folder',
  'GatewayEvent',
  'GatewayEventHandler',
  'Holiday',
  'NamedQuery',
  'NetworkDevice',
  'NetworkSegment',
  'Observer',
  'Organization',
  'Problem',
  'Project',
  'PssEventJournal',
  'PssTag',
  'QueryTag',
  'ScheduleProfile',
  'Script',
  'Site',
  'Table',
  'Tag',
  'TagProvider',
  'UDTDefinition',
  'UDTInstance',
  'UDTMember',
  'UDTParameter',
  'View',
  'ViewComponent'
])
